import { json } from "@codemirror/lang-json";
import CancelOutlined from "@mui/icons-material/CancelOutlined";
import Save from "@mui/icons-material/Save";
import {
  Box,
  IconButton,
  Paper,
  Slider,
  SxProps,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import { CasLoader } from "../../brand/CasLoader";
import CodeMirror, { Prec, keymap } from "@uiw/react-codemirror";
import ReactECharts from "echarts-for-react";
import "echarts/theme/dark";
import React, { CSSProperties, useEffect, useRef, useState } from "react";
import {
  useChart,
  useChartOptionECharts,
  useChartSourceData,
  useFetchChartListener,
  useSetChart,
} from "../../../hook/chart";
import { useIsDarkMode } from "../../../hook/setting";
import { Chart, ChartSourceData } from "../../../types/chart";
import { ChartBlock } from "../../../types/notebook/note/block/chart";
import { NoteBlockContainer } from "../../note";
import { ChartInputs } from "./chart-inputs";
import { SourceTab } from "./chart-source";

export const ChartTab: React.FC<{
  chartId: string;
  sourceData?: ChartSourceData;
}> = ({ chartId, sourceData }) => {
  const option = useChartOptionECharts(chartId, sourceData);
  const { isDarkMode } = useIsDarkMode();
  const theme = isDarkMode ? "dark" : "light";
  const [height, setHeight] = useState(300);
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (chartRef.current) {
      const echartInstance = chartRef.current.getEchartsInstance();
      echartInstance.resize({
        height: height,
      });
    }
  }, [height]);

  return option.error ? (
    <Box>
      <Typography variant="h6" color="error" gutterBottom>
        Error
      </Typography>
      <Typography variant="body1" color="textSecondary">
        {option.message}
      </Typography>
    </Box>
  ) : (
    <>
      <Slider
        value={height}
        min={300}
        max={1000}
        step={5}
        aria-label="Height"
        onChange={(e, v: any) => {
          setHeight(v as number);
        }}
      />
      {/* SVG, not canvas: axis labels become DOM text that a browser test can read
          (tests/usage-dashboard-ui). data-source-rows is the Chart Source's row count. */}
      <Box
        sx={{ height }}
        data-testid="chart-view"
        data-source-rows={sourceData ? sourceData.rows.length : ""}
      >
        <ReactECharts
          ref={chartRef}
          option={option}
          opts={{ renderer: "svg" }}
          notMerge={true}
          lazyUpdate={false}
          theme={theme}
        />
      </Box>
    </>
  );
};

export const CodeTabCode: React.FC<{ chartId: string }> = ({ chartId }) => {
  const [editing, setEditing] = useState(true);
  const [code, _setCode] = useState("");
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editControlsDisabled = !unsavedChanges || isSaving;
  const hideCircularProgessSxProps: SxProps = isSaving
    ? {}
    : { display: "none" };
  const theme = useTheme();
  const { isDarkMode } = useIsDarkMode();
  const setChartCode = useSetChart();
  const chart = useChart(chartId)!;
  const codeState = chart.code;

  useEffect(() => {
    _setCode(codeState);
  }, [codeState]);

  const setCode = (code: string) => {
    const cleanedCode = code.replace(/^```python\s*|\s*```$/g, "");
    _setCode(cleanedCode);
  };

  const onCancel = () => {
    setUnsavedChanges(false);
  };

  const onSave = () => {
    if (isSaving) return;

    setIsSaving(true);
    setChartCode({ chartId, chartData: { ...chart, code } }).then(() => {
      setIsSaving(false);
    });
  };

  const onRun = () => {
    if (isSaving) return;

    setIsSaving(true);
  };

  const codeKeyMap = Prec.highest(
    keymap.of([
      {
        key: "Ctrl-s",
        preventDefault: true,
        run: () => {
          onSave();
          return true;
        },
      },
      {
        preventDefault: true,
        stopPropagation: true,
        key: "Ctrl-Enter",
        run: () => {
          onRun();
          return true;
        },
      },
      // ...foldKeymap,
    ]),
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          height: "3rem",
        }}
      >
        <Typography variant="subtitle1">Code</Typography>
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <CasLoader
            sx={hideCircularProgessSxProps}
            size={24}
          />
          <IconButton
            color="primary"
            onClick={onSave}
            disabled={editControlsDisabled}
          >
            <Save />
          </IconButton>
          <IconButton
            color="primary"
            onClick={onCancel}
            disabled={editControlsDisabled}
          >
            <CancelOutlined />
          </IconButton>
        </Box>
      </Box>
      <CodeMirror
        editable={true}
        value={code}
        theme={isDarkMode ? "dark" : "light"}
        height="40rem"
        extensions={[codeKeyMap, json()]}
        onChange={(value: any) => {
          setUnsavedChanges(true);
          setCode(value);
        }}
      />
    </>
  );
};

export const CodeTabLogs: React.FC<{ chartId: string }> = ({ chartId }) => {
  const chart = useChart(chartId);
  const [logsLoading, setLogsLoading] = useState(false);
  const theme = useTheme();
  const { isDarkMode } = useIsDarkMode();
  const colorProps: CSSProperties = isDarkMode
    ? { backgroundColor: "rgb(29, 31, 33)", color: "rgb(197, 200, 198)" }
    : { backgroundColor: theme.palette.grey.A200 };
  const logs = "Logs!!!";

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "start",
          height: "3rem",
        }}
      >
        <Typography variant="subtitle1">Logs</Typography>
      </Box>
      {logsLoading ? (
        <Box
          sx={{ padding: "1rem", display: "flex", justifyContent: "center" }}
        >
          <CasLoader />
        </Box>
      ) : (
        <div
          style={{
            width: "100%",
            height: "40rem",
            padding: "1rem",
            overflowY: "auto",
            whiteSpace: "pre",
            ...colorProps,
          }}
        >
          {logs ? logs : "No Logs"}
        </div>
      )}
    </>
  );
};

export const CodeTab: React.FC<{ chartId: string }> = ({ chartId }) => {
  const paperStyles = {
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    height: "50rem",
    width: "100%",
    padding: 1,
  };
  return (
    <Box
      sx={{
        flexDirection: "row",
        display: "flex",
      }}
    >
      <Paper elevation={7} sx={{ ...paperStyles }}>
        <CodeTabCode chartId={chartId} />
      </Paper>
    </Box>
  );
};

export const ChartBlockComponent: React.FC<{
  block: ChartBlock;
}> = React.memo(({ block }) => {
  const chartId = block["chart-id"];
  useFetchChartListener(block);
  const chart = useChart(chartId);
  // Input Values (chart-inputs.tsx): this viewer's choices, never saved.
  const [inputValues, setInputValues] = useState<Record<string, unknown>>({});
  const sourceData = useChartSourceData(block, chart, inputValues);
  const [selectedTab, setSelectedTab] = useState(0);

  return (
    <Box
      id={chartId}
      data-testid="chart-block"
      sx={{ display: "flex", flexDirection: "row", flexGrow: 1 }}
    >
      <NoteBlockContainer name={chart?.name ?? ""} description={""}>
        <Tabs value={selectedTab}>
          <Tab
            color="primary"
            label="Chart"
            onClick={() => setSelectedTab(0)}
          />
          <Tab color="primary" label="Code" onClick={() => setSelectedTab(1)} />
          <Tab
            color="primary"
            label="Source"
            onClick={() => setSelectedTab(2)}
          />
        </Tabs>
        {chart === undefined ? (
          <></>
        ) : selectedTab === 0 ? (
          <>
            <ChartInputs block={block} chart={chart} onChange={setInputValues} />
            <ChartTab chartId={chartId} sourceData={sourceData} />
          </>
        ) : selectedTab === 1 ? (
          <CodeTab chartId={chartId} />
        ) : selectedTab === 2 ? (
          <SourceTab block={block} chart={chart} />
        ) : (
          <></>
        )}
      </NoteBlockContainer>
    </Box>
  );
});
